"use client";

import React from "react";
import { MapPin, Ship, TrendingUp, Activity } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
const hubs = [{
  id: "rotterdam",
  name: "Rotterdam",
  region: "Europe",
  x: 25,
  y: 35,
  transit: "18 Days",
  topExport: "Machinery & Chemicals",
  color: "var(--secondary)"
}, {
  id: "hamburg",
  name: "Hamburg",
  region: "Europe",
  x: 32,
  y: 28,
  transit: "20 Days",
  topExport: "Automotive Parts",
  color: "var(--secondary)"
}, {
  id: "mumbai",
  name: "Mumbai",
  region: "India",
  x: 68,
  y: 55,
  transit: "18 Days",
  topExport: "Textiles & IT Services",
  color: "var(--chart-3)"
}, {
  id: "chennai",
  name: "Chennai",
  region: "India",
  x: 75,
  y: 75,
  transit: "22 Days",
  topExport: "Engineering Goods",
  color: "var(--chart-3)"
}];
const routes = [{
  source: "rotterdam",
  target: "mumbai"
}, {
  source: "hamburg",
  target: "mumbai"
}, {
  source: "rotterdam",
  target: "chennai"
}, {
  source: "hamburg",
  target: "chennai"
}];
export default function TradeCorridorMap() {
  return <section id="trade-corridor-map" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <style>{`
        @keyframes dash-flow {
          to {
            stroke-dashoffset: -24;
          }
        }
        .animate-dash-flow {
          stroke-dasharray: 12 12;
          animation: dash-flow 1.5s linear infinite;
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.5); }
        }
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
      `}</style>

      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--primary)_0%,transparent_70%)] opacity-5 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-6 border border-secondary/20">
            <Activity className="size-4" />
            <span>Active Trade Routes</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-tight mb-6">
            Global Connectivity Matrix
          </h2>
          <p className="text-lg text-muted-foreground">
            Bridging the European Union and Indian business ecosystems. Explore our active logistics corridors facilitating seamless cross-border trade.
          </p>
        </div>

        {}
        <div className="relative w-full max-w-5xl mx-auto aspect-[4/3] sm:aspect-[2/1] bg-card/30 rounded-2xl border border-border/50 overflow-hidden shadow-2xl backdrop-blur-sm">
          
          {}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 500" preserveAspectRatio="xMidYMid slice">
            {}
            <path d="M 150 100 Q 280 50 350 180 T 250 350 Q 100 280 150 100 Z" className="fill-muted/5 stroke-muted/10" strokeWidth="2" />
            {}
            <path d="M 650 200 L 850 200 L 750 450 Z" className="fill-muted/5 stroke-muted/10" strokeWidth="2" strokeLinejoin="round" />

            {}
            {routes.map((route, i) => {
            const sourceHub = hubs.find(h => h.id === route.source);
            const targetHub = hubs.find(h => h.id === route.target);
            if (!sourceHub || !targetHub) return null;
            const x1 = sourceHub.x * 10;
            const y1 = sourceHub.y * 5;
            const x2 = targetHub.x * 10;
            const y2 = targetHub.y * 5;
            const cx = (x1 + x2) / 2;
            const cy = Math.min(y1, y2) - 100;
            const pathD = `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
            return <g key={`route-${i}`} data-index={i}>
                  {}
                  <path d={pathD} fill="none" className="stroke-muted/20" strokeWidth="2" />
                  {}
                  <path d={pathD} fill="none" className="stroke-secondary/60 animate-dash-flow" strokeWidth="2" />
                </g>;
          })}
          </svg>

          {}
          {hubs.map((hub, i) => <div key={hub.id} data-index={i} className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20" style={{
          left: `${hub.x}%`,
          top: `${hub.y}%`
        }}>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <button className="relative group p-2 outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full" aria-label={`View details for ${hub.name}`}>
                    {}
                    <span className="absolute inset-0 rounded-full animate-pulse-glow" style={{
                  backgroundColor: hub.color
                }} />
                    {}
                    <span className="relative block size-3 sm:size-4 rounded-full shadow-[0_0_15px_rgba(0,0,0,0.5)] transition-transform group-hover:scale-125" style={{
                  backgroundColor: hub.color
                }} />
                    
                    {}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 text-xs font-medium text-muted-foreground whitespace-nowrap opacity-0 sm:opacity-100 transition-opacity group-hover:text-foreground">
                      {hub.name}
                    </span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="top" align="center" className="w-64 bg-card/95 backdrop-blur-md border-border/50 shadow-xl">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-border/50 pb-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="size-4 text-secondary" />
                        <h4 className="font-semibold text-foreground">{hub.name}</h4>
                      </div>
                      <span className="text-xs font-medium px-2 py-1 rounded-md bg-muted text-muted-foreground">
                        {hub.region}
                      </span>
                    </div>
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-3">
                        <Ship className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">Avg. Transit Time</p>
                          <p className="font-medium text-foreground">{hub.transit}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <TrendingUp className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">Top Export Category</p>
                          <p className="font-medium text-foreground">{hub.topExport}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>
      </div>
    </section>;
}